<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/stores/store'
import { DialogScrollContent, DialogTitle, DialogClose} from '@/components/ui/dialog'
import SelectedItemBlock from './SelectedItemBlock.vue'
import { X } from 'lucide-vue-next'
import { IconArrowLeft } from '../icons'


const { t } = useI18n()
const store = useStore()
const { selectedFabric } = storeToRefs(store)

const initialFabrics = [
  { id: '1', name: 'Re•Studio 92 Indigo', image: '/images/fabrics/fabric_1.jpg' },
  { id: '2', name: 'Re•Studio 93 Aqua', image: '/images/fabrics/fabric_2.jpg' },
  { id: '3', name: 'Re•Studio 95 Mineral', image: '/images/fabrics/fabric_3.jpg' },
  { id: '4', name: 'Re•Studio 96 Bright Green', image: '/images/fabrics/fabric_4.jpg' },
  { id: '5', name: 'Re•Studio 96', color: 'Crystal', image: '/images/fabrics/fabric_5.jpg' },
  { id: '6', name: 'Re•Studio 98 Glass', image: '/images/fabrics/fabric_6.jpg' },
  { id: '7', name: 'Re•Studio 99 Crystal', image: '/images/fabrics/fabric_7.jpg' },
  { id: '8', name: 'Re•Studio 102', image: '/images/fabrics/fabric_8.jpg' },
  { id: '9', name: 'Re•Studio 100 Lagoon', image: '/images/fabrics/fabric_9.jpg' },
]

const initialLeather = [
  { id: '11', name: 'Re•Studio 102 Pacific', image: '/images/fabrics/fabric_1.jpg' },
  { id: '21', name: 'Re•Studio 103 Sky', image: '/images/fabrics/fabric_2.jpg' },
  { id: '31', name: 'Re•Studio 106 Midnight', image: '/images/fabrics/fabric_3.jpg' },
  { id: '41', name: 'Re•Studio 126 Onyx', image: '/images/fabrics/fabric_4.jpg' },
  { id: '51', name: 'Re•Studio 128 Typhoon', image: '/images/fabrics/fabric_5.jpg' },
  { id: '61', name: 'Re•Studio 129 Savile Grey', image: '/images/fabrics/fabric_6.jpg' },
  { id: '71', name: 'Re•Studio 130 Pelican', image: '/images/fabrics/fabric_7.jpg' },
  { id: '81', name: 'Re•Studio 131 Quartz', image: '/images/fabrics/fabric_8.jpg' },
  { id: '91', name: 'Re•Studio 132 Toned', image: '/images/fabrics/fabric_9.jpg' },
]

const fabrics = ref<{ id: string; name: string; image: string }[]>([])
const leather = ref<{ id: string; name: string; image: string }[]>([])
const activeFilter = ref('')
const materials = computed(() => {
  if (activeFilter.value === 'fabrics') return fabrics.value
  if (activeFilter.value === 'leather') return leather.value
  return [...fabrics.value, ...leather.value]
})

onMounted(() => {
  if (import.meta.env.PROD) {
    const extractMaterials = (
      selector: string,
      targetArray: { id: string; name: string; image: string }[],
    ) => {
      document.querySelectorAll(`input[data-sku^="${selector}_"]`).forEach((element) => {
        try {
          targetArray.push({
            id: element.getAttribute('id')!,
            name: element.getAttribute('data-label')!,
            image: element?.parentNode?.querySelector('img')?.getAttribute('src') ?? '',
          })
        } catch (e) {
          console.error(`${selector.toUpperCase()} ERROR`, e)
        }
      })
    }

    extractMaterials('fabric', fabrics.value)
    extractMaterials('leather', leather.value)
  } else {
    fabrics.value = [...initialFabrics]
    leather.value = [...initialLeather]
  }
})

const selectMaterial = (materialType: string) => {
  activeFilter.value = materialType
}
</script>

<template>
  <DialogScrollContent>
    <DialogClose
      class="absolute left-6 top-8 lg:left-auto lg:top-10 px-2 py-2 flex items-center gap-2 lg:right-10 p-0.5 transition-colors rounded-md hover:bg-secondary"
    >
      <X class="hidden lg:block w-7 h-7 text-black/40" />
      <IconArrowLeft class="lg:hidden text-black/40"></IconArrowLeft>
      <span class="font-semibold lg:sr-only">{{ $t('Back') }}</span>
    </DialogClose>
    <div class="fabrics-block-inner mt-16 lg:mt-0">
      <DialogTitle class="fabrics-modal-heading">
        {{ $t('Choose a color') }}
      </DialogTitle>
      <ul class="filters">
        <li :class="{ active: activeFilter === '' }">
          <button @click="() => selectMaterial('')">{{ t('All') }}</button>
        </li>
        <li :class="{ active: activeFilter === 'fabrics' }">
          <button @click="() => selectMaterial('fabrics')">{{ t('Fabric') }}</button>
        </li>
        <li :class="{ active: activeFilter === 'leather' }">
          <button @click="() => selectMaterial('leather')">{{ t('Leather') }}</button>
        </li>
      </ul>
      <div class="flex gap-10 lg:grid lg:grid-cols-12">
        <div class="fabrics-list-inner lg:col-span-8">
          <button
            v-for="fabric in materials"
            :key="fabric.id"
            class="fabric-item"
            :class="{ active: selectedFabric?.id === fabric.id }"
            @click="store.selectedFabric = fabric"
          >
            <div class="fabric-item-inner" :class="{ active: selectedFabric?.id === fabric.id }">
              <div class="fabric-image">
                <img :src="fabric.image" :alt="fabric.name" width="140" />
              </div>
              <div class="fabric-description">
                <div class="fabric-name">{{ fabric.name }}</div>
              </div>
            </div>
          </button>
        </div>
        <SelectedItemBlock />
      </div>
    </div>
  </DialogScrollContent>
</template>

<style scoped>
.modal-mask {
  position: absolute;
  z-index: 50;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  background-color: hsla(0 0 0 / 0.8);
}

.modal-wrapper {
  vertical-align: middle;
}

.modal-container {
  display: flex;
  position: absolute;
  left: 0px;
  top: 50px;
  right: 0px;
  padding: 20px;
  background-color: #fff;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.fabrics-modal-heading {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
}
.fabrics-block-inner {
  display: flex;
  justify-content: start;
  flex-direction: column;
  width: 100%;
}

.fabric-image {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}
.fabrics-list-inner {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  height: 70vh;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  padding-bottom: 16vh;
}

.fabric-item {
  cursor: pointer;
  border: 1px solid hsla(0 0 0 / 0.2);
  padding: 16px;
}

.fabric-item.active {
  border-color: hsla(0 0 0);
}

.filters {
  display: flex;
  gap: 8px;
  list-style: none;
  padding-left: 0;
  margin-bottom: 24px;
}

.filters li button {
  display: inline-flex;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  font-family: 'Mark OT', sans-serif;
  color: hsla(0 0 0 / 0.4);
  padding: 8px 16px;
  font-size: 18px;
  font-weight: 600;
}

.filters li:first-child {
  margin-left: 0;
}

.filters li.active button {
  color: hsla(0 0 0);
  text-decoration: underline;

  text-underline-offset: 6px;
  text-decoration-thickness: 2px;
}

.cross {
  float: right;
  cursor: pointer;
  font-size: 30px;
  right: 40px;
  position: absolute;
}

::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-thumb {
  background: #d1d1d1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #dedede;
}

@media (min-width: 1024px) {
  .fabrics-list-inner {
    grid-template-columns: repeat(3, 1fr);
    padding-bottom: 0;
    height: calc(60vh);
  }
  .modal-container {
    display: flex;
    left: 50px;
    top: 50px;
    right: 50px;
    bottom: 50px;
    margin: 0;
    padding: 40px;
    background-color: #fff;
    border-radius: 2px;
    transition: all 0.3s ease;
    height: fit-content;
  }
}
</style>
